import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, useSearchParams } from 'react-router'
import { ChevronDown, Eye, EyeOff, Pin, RotateCcw } from 'lucide-react'
import { toast } from 'sonner'
import { formatOdds } from '@feed-widget/utils/odds'
import BrandedFeedPreview from '../components/BrandedFeedPreview'
import {
  AdvancedDisclosure,
  Badge,
  BulkActionBar,
  Button,
  DataTable,
  Drawer,
  Field,
  IconButton,
  Input,
  LinkButton,
  PageHeader,
  PageLayout,
  PageToolbar,
  Pagination,
  SearchField,
  Select,
  StatePanel,
} from '../components/common'
import { ROLE_HIERARCHY, ROLES } from '../constants/roles'
import { useOrganization } from '../contexts/OrganizationContext'
import { extractErrorMessage } from '../lib/useApiMutation'
import { feedOpsApi, personalizedBetsApi, surfacesApi } from '../services/api'
import { POLICY_QUERY_KEY, normalizePolicy } from '../components/feeds/generatedPostsPolicy'
import { moderationHoldBadge, moderationRowsPollInterval } from '../components/moderation/moderationHoldStatus'
import styles from './GeneratedPostsPage.module.css'

const PAGE_SIZE = 20
const FILTER_DISCLOSURE_BREAKPOINT = 768
const POLICY_CONFIGURATION_PATH = '/dashboard/feed/configuration?section=generated'

const STATE_OPTIONS = [
  { value: 'live', label: 'Live' },
  { value: 'all', label: 'All states' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
]

const SOURCE_OPTIONS = [
  { value: 'personalized', label: 'All personalized bets' },
  { value: 'personalized_ticket', label: 'Ticket posts' },
  { value: 'creator_winning_ticket', label: 'Creator wins' },
  { value: 'personalized_recommendation', label: 'Crowd recommendations' },
  { value: 'winning_ticket', label: 'Winning tickets' },
]

const SORT_OPTIONS = [
  { value: 'updated_at:desc', label: 'Newest updated', sortBy: 'updated_at', sortOrder: 'desc' },
  { value: 'updated_at:asc', label: 'Oldest updated', sortBy: 'updated_at', sortOrder: 'asc' },
  { value: 'expires_at:asc', label: 'Expiring soon', sortBy: 'expires_at', sortOrder: 'asc' },
]
const DEFAULT_SORT = SORT_OPTIONS[0]
const GENERATED_FILTER_ORIGINS = new Set(SOURCE_OPTIONS.map((option) => option.value))
const GENERATED_CONTENT_ORIGINS = new Set(SOURCE_OPTIONS.slice(1).map((option) => option.value))

function firstNumber(...values) {
  for (const value of values) {
    const number = Number(value)
    if (value !== null && value !== undefined && value !== '' && Number.isFinite(number)) return number
  }
  return null
}

function positiveInteger(value, fallback = 1) {
  const parsed = Number.parseInt(value, 10)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

function errorStatus(error) {
  return error?.response?.status || error?.status || null
}

function formatDateTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString()
}

function formatCompactDateTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

function formatLag(seconds) {
  if (seconds === null || seconds === undefined) return 'Unknown'
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))} sec`
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`
  return `${Math.round(seconds / 3600)} hr`
}

function formatDateTimeInput(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (part) => String(part).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function sourceLabel(origin) {
  if (origin === 'creator_winning_ticket') return 'Creator win'
  if (origin === 'personalized_recommendation') return 'Crowd recommendation'
  if (origin === 'winning_ticket') return 'Winning ticket'
  return 'Ticket post'
}

function sourceTone(origin) {
  if (origin === 'creator_winning_ticket' || origin === 'winning_ticket') return 'success'
  return origin === 'personalized_recommendation' ? 'info' : 'neutral'
}

function isGeneratedPost(post) {
  return GENERATED_CONTENT_ORIGINS.has(post?.content_origin)
}

function eligibilityLabel(reason) {
  if (!reason || reason === 'eligible') return 'Eligible for publication'
  const label = String(reason).replaceAll('_', ' ')
  return `${label.charAt(0).toUpperCase()}${label.slice(1)}`
}

function selectionsForPost(post) {
  const candidates = [
    post?.selections,
    post?.selection_set?.selections,
    post?.bet_legs,
    post?.feed_payload?.selection_set?.selections,
    post?.feed_payload?.legs,
  ]
  return candidates.find(Array.isArray) || []
}

function eventName(selection) {
  return selection?.event_name || selection?.event?.name || selection?.selection?.event_name || ''
}

function selectionName(selection) {
  return selection?.label || selection?.selection_name || selection?.selection?.name || 'Selection'
}

function sportName(post) {
  const selection = selectionsForPost(post)[0]
  return post?.sport_name
    || post?.sport
    || selection?.sport_name
    || selection?.sport
    || selection?.event?.sport
    || selection?.event?.metadata?.sport
    || '—'
}

function eventSummary(post) {
  const explicit = Array.isArray(post?.events) ? post.events : []
  const names = [...explicit, ...selectionsForPost(post).map(eventName)]
    .map((event) => typeof event === 'string' ? event : event?.name)
    .filter(Boolean)
  const unique = [...new Set(names)]
  if (!unique.length) return '—'
  if (unique.length <= 2) return unique.join(' · ')
  return `${unique.slice(0, 2).join(' · ')} +${unique.length - 2}`
}

function postSummary(post) {
  // Winning-ticket cards carry no text; their title names the card ("2-leg winner").
  return post?.bet_summary || post?.summary || post?.text || post?.title || post?.feed_payload?.text || 'Generated bet'
}

function inspectorPreviewItem(post) {
  if (post?.creator || post?.user || post?.feed_payload?.creator || post?.feed_payload?.user) return post
  return { ...post, creator: { display_name: 'Personalized Bets' } }
}

function postOdds(post) {
  return formatOdds(
    post?.display_odds
      || post?.aggregate_odds
      || post?.total_odds
      || post?.selection_set?.summary?.odds
      || post?.feed_payload?.selection_set?.summary?.odds,
  )
}

function supportCount(post) {
  if (post?.content_origin !== 'personalized_recommendation') return '—'
  return firstNumber(post?.support_count, post?.recommendation_support_count, post?.rank_inputs?.support_count) ?? '—'
}

function postState(post) {
  const pipelineState = post?.pipeline_state || post?.denorm_status || post?.state
  if (pipelineState === 'failed' || post?.failed) return { label: 'Failed', tone: 'danger' }
  // A post moderation holds, rejected or never checked reads by its
  // moderation state, never as live.
  const moderationBadge = moderationHoldBadge(post?.moderation)
  if (moderationBadge) return { label: moderationBadge.label, tone: moderationBadge.tone }
  if (pipelineState === 'pending' || post?.live_state === 'pending' || post?.approval_status === 'pending_approval') return { label: 'Pending', tone: 'warning' }
  if (post?.not_live_reason === 'expired' || (post?.expires_at && Date.parse(post.expires_at) <= Date.now())) return { label: 'Expired', tone: 'neutral' }
  if (post?.is_hidden || post?.live_state === 'hidden') return { label: 'Hidden', tone: 'warning' }
  return { label: 'Live', tone: 'success' }
}

function feedPosition(post) {
  const override = post?.order_override || post?.override
  const position = firstNumber(override?.manual_rank, post?.feed_position, post?.position)
  if (override?.pin_to_top || post?.is_pinned) return position === null ? 'Pinned' : `Pinned · ${position}`
  return position === null ? 'Algorithm' : position
}

function normalizedPosts(response) {
  const posts = response?.posts || response?.items || (Array.isArray(response) ? response : [])
  return Array.isArray(posts) ? posts : []
}

function surfaceTargets(post) {
  const targets = post?.surface_targets || post?.surfaces || []
  if (!Array.isArray(targets)) return []
  return targets.map((surface) => typeof surface === 'string' ? surface : surface?.name || surface?.slug).filter(Boolean)
}

function surfaceTargetSlugs(post) {
  const targets = post?.surface_targets || post?.surfaces || []
  if (!Array.isArray(targets)) return []
  return targets.map((target) => typeof target === 'string' ? target : target?.slug).filter(Boolean)
}


function statusPayload(response) {
  return response?.status || response || {}
}

function pipelineMetrics(status) {
  const activeTickets = firstNumber(status.active_ticket_post_count, status.active_ticket_posts, status.counts?.active_tickets) ?? 0
  const activeRecommendations = firstNumber(status.active_recommendation_post_count, status.active_recommendation_posts, status.counts?.active_recommendations) ?? 0
  const hidden = firstNumber(status.hidden_post_count, status.hidden_posts, status.counts?.hidden) ?? 0
  const pending = firstNumber(status.pending_post_count, status.pending_posts, status.counts?.pending) ?? 0
  const failed = firstNumber(status.failed_post_count, status.failed_posts, status.counts?.failed) ?? 0
  const lagSeconds = firstNumber(status.cursor_lag_seconds, status.cursor?.lag_seconds, status.lag_seconds)
  const projectionOffsetLag = firstNumber(status.projection_offset_lag) ?? 0
  const blockedArchiveBatches = firstNumber(status.blocked_archive_batch_count) ?? 0
  const unprojectedArchiveBatches = firstNumber(status.unprojected_archive_batch_count) ?? 0
  const ingested = firstNumber(status.ingested_bet_count, status.recent_user_bet_count, status.counts?.recent_user_bets)
  const eligible = firstNumber(status.eligible_ticket_count, status.eligible_candidate_count, status.eligible_count, status.counts?.eligible)
  const recentOnline = firstNumber(status.recent_online_bet_count) ?? 0
  const nonOpenOrLive = firstNumber(status.non_open_or_live_count) ?? 0
  const unresolved = firstNumber(status.unresolved_count) ?? 0
  const unavailable = firstNumber(status.unavailable_count) ?? 0
  const lockedOrExpired = firstNumber(status.locked_or_expired_count) ?? 0
  const exactDuplicates = firstNumber(status.exact_duplicate_count) ?? 0
  const distinctTicketShapes = firstNumber(status.distinct_ticket_shape_count) ?? 0
  const deferredByCapacity = firstNumber(status.capacity_deferred_shape_count) ?? 0
  const latestIngest = status.latest_ingest || {}
  const batchFetched = firstNumber(latestIngest.fetched_count)
  const batchInserted = firstNumber(latestIngest.inserted_count)
  const batchUpdated = firstNumber(latestIngest.updated_count)
  const batchRetailExcluded = firstNumber(latestIngest.retail_excluded_count)
  const batchOtherSkipped = firstNumber(latestIngest.other_skipped_count)
  return {
    activeTickets,
    activeRecommendations,
    hidden,
    pending,
    failed,
    lagSeconds,
    projectionOffsetLag,
    blockedArchiveBatches,
    unprojectedArchiveBatches,
    ingested,
    eligible,
    recentOnline,
    nonOpenOrLive,
    unresolved,
    unavailable,
    lockedOrExpired,
    exactDuplicates,
    distinctTicketShapes,
    deferredByCapacity,
    batchFetched,
    batchInserted,
    batchUpdated,
    batchRetailExcluded,
    batchOtherSkipped,
    batchCompletedAt: latestIngest.completed_at,
  }
}

function sourceIsEnabled(status) {
  return Boolean(status.enabled ?? status.source_enabled ?? status.source?.enabled)
}

function sourceIsStale(status) {
  return Boolean(
    status.cursor_stale
      || status.is_stale
      || status.cursor?.stale
      || status.pipeline_state === 'stale'
      || status.state === 'stale',
  )
}


function GeneratedPostsWorkspace() {
  const { currentOrg } = useOrganization()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const inspectorReturnRef = useRef(null)
  const inventoryHeadingRef = useRef(null)
  const [selectedRows, setSelectedRows] = useState([])
  const selectedRowsRef = useRef([])
  const generatedPollTrackerRef = useRef(null)
  const [inspectedPost, setInspectedPost] = useState(null)
  const [pinUntil, setPinUntil] = useState('')
  const [pinSurface, setPinSurface] = useState('')
  const [pinRank, setPinRank] = useState(1)
  const [successMessage, setSuccessMessage] = useState('')
  const [compactFilters, setCompactFilters] = useState(() => (
    typeof window !== 'undefined' && window.innerWidth <= FILTER_DISCLOSURE_BREAKPOINT
  ))
  const [filtersExpanded, setFiltersExpanded] = useState(false)
  const [operationError, setOperationError] = useState('')

  const clearSelectedRows = useCallback((restoreFocus = false) => {
    selectedRowsRef.current = []
    setSelectedRows([])
    if (restoreFocus) window.queueMicrotask(() => inventoryHeadingRef.current?.focus())
  }, [])

  const handleSelectionChange = useCallback((rows) => {
    selectedRowsRef.current = rows
    setSelectedRows(rows)
  }, [])


  useEffect(() => {
    const update = () => setCompactFilters(window.innerWidth <= FILTER_DISCLOSURE_BREAKPOINT)
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])
  const selectedKeys = useMemo(
    () => new Set(selectedRows.map((post) => post.id)),
    [selectedRows],
  )

  const role = currentOrg?.role || ROLES.VIEWER
  const canManage = (ROLE_HIERARCHY[role] || 0) >= ROLE_HIERARCHY[ROLES.EXTERNAL_ORG_ADMIN]
  const orgId = currentOrg?.id
  const orgSlug = currentOrg?.slug
  const page = positiveInteger(searchParams.get('page'))
  const query = searchParams.get('q') || ''
  const surface = searchParams.get('surface') || ''
  const state = searchParams.get('state') || 'live'
  const requestedOrigin = searchParams.get('origin') || 'personalized'
  const origin = GENERATED_FILTER_ORIGINS.has(requestedOrigin) ? requestedOrigin : 'personalized'
  const sport = searchParams.get('sport') || ''
  const requestedSort = `${searchParams.get('sort_by') || DEFAULT_SORT.sortBy}:${searchParams.get('sort_order') || DEFAULT_SORT.sortOrder}`
  const sortOption = SORT_OPTIONS.find((option) => option.value === requestedSort) || DEFAULT_SORT

  const setFilter = (key, value) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      if (value) next.set(key, value)
      else next.delete(key)
      if (key !== 'page') next.set('page', '1')
      return next
    })
  }

  const clearBetFilters = () => {
    setSearchParams({}, { replace: true })
  }

  const setSort = (value) => {
    const nextSort = SORT_OPTIONS.find((option) => option.value === value) || DEFAULT_SORT
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      if (nextSort === DEFAULT_SORT) {
        next.delete('sort_by')
        next.delete('sort_order')
      } else {
        next.set('sort_by', nextSort.sortBy)
        next.set('sort_order', nextSort.sortOrder)
      }
      next.set('page', '1')
      return next
    }, { replace: true })
  }

  const listParams = useMemo(() => ({
    origin,
    view: state,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    sort_by: sortOption.sortBy,
    sort_order: sortOption.sortOrder,
    ...(surface ? { surface } : {}),
    ...(sport ? { sport } : {}),
    ...(query.trim() ? { q: query.trim() } : {}),
  }), [origin, page, query, sortOption.sortBy, sortOption.sortOrder, sport, state, surface])


  const statusQuery = useQuery({
    queryKey: ['personalized-bets-status', orgId],
    queryFn: () => personalizedBetsApi.getStatus(orgId),
    enabled: Boolean(orgId && canManage),
    refetchInterval: (query) => (query.state.data?.health_source === 'stale' ? 15_000 : false),
  })

  const policyQuery = useQuery({
    queryKey: [POLICY_QUERY_KEY, orgId],
    queryFn: () => personalizedBetsApi.getPolicy(orgId),
    enabled: Boolean(orgId && canManage),
  })

  const postsQuery = useQuery({
    queryKey: ['personalized-bets-posts', orgSlug, listParams],
    queryFn: () => personalizedBetsApi.list(orgSlug, listParams),
    enabled: Boolean(orgSlug && canManage),
    placeholderData: (previous, previousQuery) => previousQuery?.queryKey?.[1] === orgSlug ? previous : undefined,
    // Refetch while a row's moderation badge could change on its own, as
    // Manage posts does.
    refetchInterval: (query) => {
      const rows = (query.state.data?.posts || []).map((post) => ({ key: post.id, moderation: post.moderation }))
      const { interval, tracker } = moderationRowsPollInterval(rows, generatedPollTrackerRef.current)
      generatedPollTrackerRef.current = tracker
      return interval
    },
  })


  const surfacesQuery = useQuery({
    queryKey: ['surfaces', orgSlug],
    queryFn: () => surfacesApi.list(orgSlug),
    enabled: Boolean(orgSlug && canManage),
    staleTime: 60_000,
  })

  const orderDetailQuery = useQuery({
    queryKey: ['personalized-bet-order-detail', orgSlug, inspectedPost?.id, pinSurface],
    queryFn: () => feedOpsApi.getPostDetail(orgSlug, inspectedPost.id, { surface: pinSurface }),
    enabled: Boolean(orgSlug && inspectedPost?.id && pinSurface && canManage),
  })

  useEffect(() => {
    if (requestedOrigin === origin) return
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      next.delete('origin')
      next.set('page', '1')
      return next
    }, { replace: true })
  }, [origin, requestedOrigin, setSearchParams])

  useEffect(() => {
    if (requestedSort === sortOption.value) return
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      next.delete('sort_by')
      next.delete('sort_order')
      next.set('page', '1')
      return next
    }, { replace: true })
  }, [requestedSort, setSearchParams, sortOption.value])

  useEffect(() => {
    if (!selectedRowsRef.current.length) return
    clearSelectedRows()
  }, [clearSelectedRows, orgSlug, origin, page, query, sortOption.value, sport, state, surface])

  useEffect(() => {
    setInspectedPost(null)
    inspectorReturnRef.current = null
    setOperationError('')
    setSuccessMessage('')
  }, [orgId, orgSlug])

  const invalidateWorkspace = () => {
    queryClient.invalidateQueries({ queryKey: ['personalized-bets-posts', orgSlug] })
    queryClient.invalidateQueries({ queryKey: ['personalized-bets-status', orgId] })
  }

  const visibilityMutation = useMutation({
    mutationFn: ({ post, isHidden }) => {
      if (!isGeneratedPost(post)) throw new Error('Only generated personalized bets can be moderated here.')
      return feedOpsApi.updatePost(orgSlug, post.id, { is_hidden: isHidden })
    },
    onMutate: () => { setOperationError(''); setSuccessMessage('') },
    onSuccess: (_response, variables) => {
      setSuccessMessage(variables.isHidden ? 'Personalized bet hidden.' : 'Personalized bet restored.')
      toast.success(variables.isHidden ? 'Personalized bet hidden' : 'Personalized bet restored')
      invalidateWorkspace()
    },
    onError: (error) => setOperationError(extractErrorMessage(error, 'Visibility could not be updated.')),
  })

  const bulkVisibilityMutation = useMutation({
    mutationFn: (isHidden) => {
      const postIds = selectedRows.filter(isGeneratedPost).map((post) => post.id)
      if (postIds.length !== selectedRows.length) throw new Error('Only generated personalized bets can be moderated here.')
      return feedOpsApi.batchVisibility(orgSlug, {
        post_ids: postIds,
        is_hidden: isHidden,
      })
    },
    onMutate: () => { setOperationError(''); setSuccessMessage('') },
    onSuccess: (response, isHidden) => {
      const count = Math.max(0, Number(response?.updated) || 0)
      setSuccessMessage(count === 0
        ? 'No personalized bets were updated.'
        : `${count} personalized ${count === 1 ? 'bet' : 'bets'} ${isHidden ? 'hidden' : 'restored'}.`)
      clearSelectedRows(true)
      invalidateWorkspace()
    },
    onError: (error) => setOperationError(extractErrorMessage(error, 'Selected visibility could not be updated.')),
  })

  const pinMutation = useMutation({
    mutationFn: ({ post, applyUntil, targetSurface, manualRank }) => {
      if (!isGeneratedPost(post)) throw new Error('Only generated personalized bets can be pinned here.')
      return feedOpsApi.upsertOrder(orgSlug, {
        surface: targetSurface,
        items: [{
          post_id: post.id,
          manual_rank: manualRank,
          pin_to_top: true,
          apply_until: new Date(applyUntil).toISOString(),
          notes: 'Personalized bets workspace pin',
        }],
      })
    },
    onMutate: () => { setOperationError(''); setSuccessMessage('') },
    onSuccess: (_response, variables) => {
      const override = {
        surface: variables.targetSurface,
        manual_rank: variables.manualRank,
        pin_to_top: true,
        apply_until: new Date(variables.applyUntil).toISOString(),
      }
      setInspectedPost((current) => current?.id === variables.post.id ? {
        ...current,
        is_pinned: true,
        order_override: override,
        override,
      } : current)
      const detailKey = ['personalized-bet-order-detail', orgSlug, variables.post.id, variables.targetSurface]
      queryClient.setQueryData(detailKey, (current) => ({
        ...(current || {}),
        post: { ...(current?.post || {}), override },
      }))
      queryClient.invalidateQueries({ queryKey: detailKey })
      setSuccessMessage('Time-bound pin saved.')
      toast.success('Time-bound pin saved')
      invalidateWorkspace()
    },
    onError: (error) => setOperationError(extractErrorMessage(error, 'Pin could not be saved.')),
  })

  const resetPinMutation = useMutation({
    mutationFn: ({ post, targetSurface }) => {
      if (!isGeneratedPost(post)) throw new Error('Only generated personalized bets can be reset here.')
      return feedOpsApi.resetOrder(orgSlug, post.id, { surface: targetSurface })
    },
    onMutate: () => { setOperationError(''); setSuccessMessage('') },
    onSuccess: (_response, variables) => {
      setInspectedPost((current) => current?.id === variables.post.id ? {
        ...current,
        is_pinned: false,
        order_override: null,
        override: null,
      } : current)
      const detailKey = ['personalized-bet-order-detail', orgSlug, variables.post.id, variables.targetSurface]
      queryClient.setQueryData(detailKey, (current) => ({
        ...(current || {}),
        post: { ...(current?.post || {}), override: null },
      }))
      queryClient.invalidateQueries({ queryKey: detailKey })
      setSuccessMessage('Pin reset to algorithmic ranking.')
      toast.success('Pin reset')
      invalidateWorkspace()
    },
    onError: (error) => setOperationError(extractErrorMessage(error, 'Pin could not be reset.')),
  })

  const status = statusPayload(statusQuery.data)
  const metrics = pipelineMetrics(status)
  const activeInventory = metrics.activeTickets + metrics.activeRecommendations
  const attentionCount = metrics.hidden + metrics.pending + metrics.failed
  const enabled = sourceIsEnabled(status)
  const stale = sourceIsStale(status)
  const healthUpdated = status.health_computed_at ? formatDateTime(status.health_computed_at) : ''
  const projectionBlocked = status.state === 'blocked' || metrics.blockedArchiveBatches > 0
  const projectionBacklogged = status.state === 'backlogged'
  const posts = normalizedPosts(postsQuery.data).filter(isGeneratedPost)
  const total = firstNumber(postsQuery.data?.total, postsQuery.data?.count) ?? (postsQuery.isSuccess ? posts.length : null)
  const policy = policyQuery.data ? normalizePolicy(policyQuery.data) : null
  const feedTarget = policy ? policy.ranking : null
  // Winning tickets are generated from settled outcomes, independently of the
  // personalized-bets source, so their inventory stays reachable without it.
  const winningTicketsEnabled = Boolean(policy?.winning_tickets.enabled)
  const pageCount = Math.max(1, Math.ceil((total ?? 0) / PAGE_SIZE))
  const clampingPage = postsQuery.isSuccess && page > pageCount

  useEffect(() => {
    if (!clampingPage) return
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      if (pageCount === 1) next.delete('page')
      else next.set('page', String(pageCount))
      return next
    }, { replace: true })
  }, [clampingPage, pageCount, setSearchParams])
  const allSurfaces = surfacesQuery.data?.items || []
  const surfaces = allSurfaces.filter((item) => item.is_enabled !== false)
  const defaultSurface = surfaces.find((item) => item.include_by_default !== false) || surfaces[0] || null
  const sports = [...new Set([
    ...(Array.isArray(status.sports) ? status.sports : []),
    ...posts.map(sportName),
  ].filter((name) => name && name !== '—'))].sort((a, b) => a.localeCompare(b))
  const activeFilterCount = [surface, state !== 'live', sport, origin !== 'personalized', sortOption !== DEFAULT_SORT].filter(Boolean).length
  const activeFilters = Boolean(query || activeFilterCount)
  const noIngest = enabled && (status.state === 'no_data' || metrics.ingested === 0)
  const noEligible = enabled && (status.state === 'no_eligible' || (metrics.ingested !== null && metrics.ingested > 0 && metrics.eligible === 0))
  const pipelineBadge = statusQuery.isLoading
    ? <Badge>Checking status</Badge>
    : projectionBlocked
      ? <Badge tone="danger">Source blocked</Badge>
      : projectionBacklogged
        ? <Badge tone="warning">Updates delayed</Badge>
        : stale
          ? <Badge tone="warning">Update overdue</Badge>
          : enabled
            ? <Badge tone="success">Enabled</Badge>
            : <Badge tone="neutral">Disabled</Badge>

  const orderOverride = orderDetailQuery.isSuccess ? orderDetailQuery.data?.post?.override || null : null

  useEffect(() => {
    if (!inspectedPost || !pinSurface || !orderDetailQuery.isSuccess) return
    setPinUntil(formatDateTimeInput(orderOverride?.apply_until))
    setPinRank(firstNumber(orderOverride?.manual_rank, inspectedPost.feed_position, inspectedPost.position) || 1)
  }, [inspectedPost, orderDetailQuery.isSuccess, orderOverride, pinSurface])

  const openInspector = useCallback((post, returnTarget) => {
    if (!isGeneratedPost(post)) {
      setOperationError('Only generated personalized bets can be inspected here.')
      return
    }
    const override = post.order_override || post.override
    inspectorReturnRef.current = returnTarget || document.activeElement
    setPinUntil(formatDateTimeInput(override?.apply_until))
    setPinRank(firstNumber(override?.manual_rank, post.feed_position, post.position) || 1)
    setPinSurface(surface || surfaceTargetSlugs(post)[0] || defaultSurface?.slug || '')
    setInspectedPost(post)
  }, [defaultSurface?.slug, surface])

  const savePin = () => {
    const applyUntil = new Date(pinUntil)
    const manualRank = Number(pinRank)
    if (!pinSurface) {
      setOperationError('Choose a surface before saving the pin.')
      return
    }
    if (orderDetailQuery.isError) {
      setOperationError('Pin settings could not load. Retry them before saving.')
      return
    }
    if (!Number.isInteger(manualRank) || manualRank < 1) {
      setOperationError('Feed position must be a positive whole number.')
      return
    }
    if (!pinUntil || Number.isNaN(applyUntil.getTime()) || applyUntil.getTime() <= Date.now()) {
      setOperationError('Pin end time must be in the future.')
      return
    }
    pinMutation.mutate({ post: inspectedPost, applyUntil: pinUntil, targetSurface: pinSurface, manualRank })
  }

  const {
    isPending: visibilityPending,
    mutate: mutateVisibility,
    variables: visibilityVariables,
  } = visibilityMutation

  const columns = useMemo(() => [
    {
      key: 'summary',
      label: 'Bet',
      priority: 'primary',
      className: styles.betColumn,
      render: (post) => (
        <div className={styles.betCell}>
          <strong>{postSummary(post)}</strong>
          <div className={styles.betMetadata}>
            <Badge compact tone={sourceTone(post.content_origin)}>{sourceLabel(post.content_origin)}</Badge>
            <span>{sportName(post)}</span>
            <span>{selectionsForPost(post).length} {selectionsForPost(post).length === 1 ? 'selection' : 'selections'}</span>
            <span>{eventSummary(post)}</span>
          </div>
        </div>
      ),
    },
    { key: 'odds', label: 'Odds', priority: 'secondary', render: postOdds, numeric: true },
    { key: 'support', label: 'Support', priority: 'secondary', render: supportCount, numeric: true },
    {
      key: 'state',
      label: 'State',
      priority: 'primary',
      className: styles.stateColumn,
      render: (post) => {
        const current = postState(post)
        return <Badge compact tone={current.tone}>{current.label}</Badge>
      },
    },
    { key: 'feed_position', label: 'Position', priority: 'secondary', render: feedPosition },
    { key: 'expires_at', label: 'Expires', priority: 'tertiary', render: (post) => formatCompactDateTime(post.expires_at) },
    {
      key: 'actions',
      label: 'Actions',
      priority: 'primary',
      className: styles.actionsColumn,
      render: (post) => (
        <div className={styles.rowActions} onClick={(event) => event.stopPropagation()}>
          <IconButton label="Preview" variant="ghost" onClick={(event) => openInspector(post, event.currentTarget)}>
            <Eye size={16} aria-hidden="true" />
          </IconButton>
          <IconButton
            label={post.is_hidden ? 'Unhide' : 'Hide'}
            variant="ghost"
            disabled={visibilityPending && visibilityVariables?.post?.id === post.id}
            onClick={() => mutateVisibility({ post, isHidden: !post.is_hidden })}
          >
            {post.is_hidden ? <Eye size={16} aria-hidden="true" /> : <EyeOff size={16} aria-hidden="true" />}
          </IconButton>
        </div>
      ),
    },
  ], [mutateVisibility, openInspector, visibilityPending, visibilityVariables])

  if (!orgId || !orgSlug) {
    return (
      <PageLayout size="workspace" className={styles.page}>
        <PageHeader title="Generated posts" mode="workspace" />
        <StatePanel state="restricted" title="No organization selected" description="Choose an organization to manage generated posts." />
      </PageLayout>
    )
  }


  const loadError = statusQuery.error || postsQuery.error || surfacesQuery.error
  const loading = statusQuery.isLoading || postsQuery.isLoading || surfacesQuery.isLoading || clampingPage
  const restrictedError = [statusQuery.error, postsQuery.error, surfacesQuery.error].some((error) => [401, 403].includes(errorStatus(error)))

  return (
    <PageLayout size="workspace" className={styles.page}>
      <PageHeader
        title="Generated posts"
        context={currentOrg?.name || orgSlug}
        status={pipelineBadge}
        mode="workspace"
        actions={canManage ? (
          <LinkButton href={POLICY_CONFIGURATION_PATH} variant="secondary">
            Configure publishing policy
          </LinkButton>
        ) : null}
      />

      <div className={styles.viewPanel}>
      {!canManage ? (
        <StatePanel
          state="restricted"
          title="Organization admin access required"
          description="Ask an organization admin to manage personalized bets and publishing policy."
        />
      ) : restrictedError ? (
        <StatePanel state="restricted" title="Personalized bets access is restricted" description="Your current session cannot inspect or manage this source." />
      ) : loading ? (
        <StatePanel state="loading" title="Loading workspace…" description="Loading the selected workspace." />
      ) : loadError ? (
        <StatePanel
          state="error"
          title="Workspace could not load"
          description={extractErrorMessage(loadError, 'The workspace was not changed.')}
          onRetry={() => {
            statusQuery.refetch()
            postsQuery.refetch()
            surfacesQuery.refetch()
          }}
        />
      ) : (
        <>
          <div className={styles.overviewBlock}>
            <section className={styles.overview} aria-label="Personalized bets overview">
              <div className={styles.overviewItem} aria-label={`Active inventory: ${activeInventory}`}>
                <span className={styles.overviewLabel}>Active inventory</span>
                <strong className={styles.overviewValue}>{activeInventory}</strong>
                <span className={styles.overviewDetail}>{metrics.activeTickets} ticket posts · {metrics.activeRecommendations} recommendations</span>
              </div>
              <div className={styles.overviewItem} aria-label={`Needs attention: ${attentionCount}`}>
                <span className={styles.overviewLabel}>Needs attention</span>
                <strong className={styles.overviewValue}>{attentionCount}</strong>
                <span className={styles.overviewDetail}>{metrics.hidden} hidden · {metrics.pending} pending · {metrics.failed} failed</span>
              </div>
              <div className={styles.overviewItem} aria-label={`Source activity: ${metrics.ingested ?? 'unknown'}`}>
                <span className={styles.overviewLabel}>Source activity</span>
                <strong className={styles.overviewValue}>{metrics.ingested ?? '—'}</strong>
                <span className={styles.overviewDetail}>{metrics.eligible ?? '—'} eligible · {metrics.projectionOffsetLag} queued · {formatLag(metrics.lagSeconds)} update delay</span>
              </div>
              <div className={styles.overviewItem} aria-label={`Feed target: ${feedTarget?.target_personalized_first_10 ?? 'unknown'} of the first 10`}>
                <span className={styles.overviewLabel}>Feed target</span>
                <strong className={styles.overviewValue}>{feedTarget?.target_personalized_first_10 ?? '—'}<span> / 10</span></strong>
                <span className={styles.overviewDetail}>Maximum {feedTarget?.max_consecutive_personalized ?? '—'} in a row</span>
              </div>
            </section>
            {healthUpdated ? <p className={styles.healthUpdated}>Updated {healthUpdated}{status.health_source === 'stale' ? ' · refreshing' : ''}</p> : null}
          </div>

          {successMessage ? <StatePanel compact state="success" title={successMessage} /> : null}
          {operationError ? (
            <StatePanel
              compact
              state={[401, 403].includes(errorStatus(visibilityMutation.error || bulkVisibilityMutation.error || pinMutation.error || resetPinMutation.error)) ? 'restricted' : 'error'}
              title={[401, 403].includes(errorStatus(visibilityMutation.error || bulkVisibilityMutation.error || pinMutation.error || resetPinMutation.error)) ? 'Action not permitted' : 'Action failed'}
              description={operationError}
            />
          ) : null}
          {projectionBlocked ? (
            <StatePanel
              compact
              state="error"
              title="Source updates are blocked"
              description={`One source batch could not be processed. ${metrics.projectionOffsetLag} updates across ${metrics.unprojectedArchiveBatches} batches are waiting. Resolve the failed batch before publishing.`}
              onRetry={statusQuery.refetch}
              retryLabel="Refresh status"
            />
          ) : null}
          {projectionBacklogged && !projectionBlocked ? (
            <StatePanel
              compact
              state="error"
              title="Source updates are delayed"
              description={`${metrics.projectionOffsetLag} updates across ${metrics.unprojectedArchiveBatches} batches are waiting. Let processing catch up before publishing.`}
              onRetry={statusQuery.refetch}
              retryLabel="Refresh status"
            />
          ) : null}
          {stale && !projectionBlocked && !projectionBacklogged ? (
            <StatePanel
              compact
              state="error"
              title="Source update is overdue"
              description={`No source update has completed for ${formatLag(metrics.lagSeconds)}. Existing posts remain available for moderation.`}
              onRetry={statusQuery.refetch}
              retryLabel="Refresh status"
            />
          ) : null}
          {noEligible && enabled && !noIngest ? (
            <StatePanel
              compact
              state="empty"
              title="No eligible candidates"
              description="No recent source bets meet the publication rules. Existing posts remain available for moderation."
              onRetry={statusQuery.refetch}
              retryLabel="Refresh eligibility"
            />
          ) : null}


          {enabled ? (
            <details className={styles.funnelDisclosure} aria-label="Candidate diagnostics">
              <summary>
                <span className={styles.funnelSummaryCopy}>
                  <strong>Candidate diagnostics</strong>
                  <span>{metrics.eligible ?? 0} eligible · {metrics.distinctTicketShapes} unique combinations</span>
                </span>
                <ChevronDown className={styles.funnelChevron} size={16} aria-hidden="true" />
              </summary>
              <div className={styles.funnelContent}>
                <section className={styles.funnelGroup} aria-labelledby="candidate-eligibility-heading">
                  <div className={styles.funnelGroupHeading}>
                    <h2 id="candidate-eligibility-heading">Eligibility</h2>
                    <p>Last 72 hours</p>
                  </div>
                  <dl className={styles.funnelList}>
                    {[
                      ['Recent online bets', metrics.recentOnline],
                      ['Not open or live', metrics.nonOpenOrLive],
                      ['Unresolved', metrics.unresolved],
                      ['Unavailable', metrics.unavailable],
                      ['Locked or expired', metrics.lockedOrExpired],
                      ['Eligible ticket posts', metrics.eligible ?? 0],
                      ['Exact duplicates', metrics.exactDuplicates],
                      ['Unique ticket combinations', metrics.distinctTicketShapes],
                      ['Deferred by capacity', metrics.deferredByCapacity],
                      ['Published ticket posts', metrics.activeTickets],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <dt>{label}</dt>
                        <dd>{value}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
                <section className={styles.funnelGroup} aria-labelledby="latest-ingest-heading">
                  <div className={styles.funnelGroupHeading}>
                    <h2 id="latest-ingest-heading">Latest source update</h2>
                    <p>Completed {formatDateTime(metrics.batchCompletedAt)}</p>
                  </div>
                  <dl className={styles.batchList}>
                    {[
                      ['Fetched', metrics.batchFetched],
                      ['Inserted', metrics.batchInserted],
                      ['Updated', metrics.batchUpdated],
                      ['Retail excluded', metrics.batchRetailExcluded],
                      ['Other skipped', metrics.batchOtherSkipped],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <dt>{label}</dt>
                        <dd>{value ?? '—'}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              </div>
              <p className={styles.funnelNote}>Retail records cannot be copied online. Unique ticket combinations—not raw bet volume—limit published inventory.</p>
            </details>
          ) : null}

          {!enabled && winningTicketsEnabled ? (
            <StatePanel
              compact
              state="disabled"
              title="Personalized bets source is disabled"
              description="Winning tickets are still generated from settled results and are listed below."
            />
          ) : null}
          {!enabled && !winningTicketsEnabled ? (
            <StatePanel
              state="disabled"
              title="Personalized bets source is disabled"
              description="A superadmin must enable the source in Settings. Organization admins cannot enable or reconfigure it here."
            />
          ) : noIngest && !winningTicketsEnabled ? (
            <StatePanel
              state="empty"
              title="No source bets yet"
              description="Bets will appear after the next eligible source update."
            />
          ) : (
            <section className={styles.workspace} aria-labelledby="personalized-posts-heading">
              <div className={styles.sectionHeading}>
                <div>
                  <h2 ref={inventoryHeadingRef} id="personalized-posts-heading" tabIndex={-1}>Bet inventory</h2>
                  <p>{total} generated {total === 1 ? 'bet' : 'bets'} match the current scope.</p>
                </div>
              </div>

              <PageToolbar
                className={styles.toolbar}
                label="Personalized bets filters and bulk actions"
                search={(
                  <SearchField
                    value={query}
                    onChange={(value) => setFilter('q', value)}
                    placeholder="Search bets or events…"
                    label="Search personalized bets"
                    loading={postsQuery.isFetching && !postsQuery.isLoading}
                  />
                )}
                filters={(
                  <details
                    className={styles.filterDisclosure}
                    aria-label="Filters"
                    open={!compactFilters || filtersExpanded}
                    onToggle={(event) => {
                      if (compactFilters) setFiltersExpanded(event.currentTarget.open)
                    }}
                  >
                    <summary>
                      <span className={styles.filterSummaryCopy}>
                        <strong>Filters</strong>
                        {activeFilterCount ? <span>{activeFilterCount} active</span> : null}
                      </span>
                      <ChevronDown className={styles.filterChevron} size={16} aria-hidden="true" />
                    </summary>
                    <div className={styles.filterGrid}>
                    <Select aria-label="Surface" value={surface} onChange={(event) => setFilter('surface', event.target.value)}>
                      <option value="">All surfaces</option>
                      {surfaces.map((item) => <option key={item.id || item.slug} value={item.slug}>{item.name}</option>)}
                    </Select>
                    <Select aria-label="State" value={state} onChange={(event) => setFilter('state', event.target.value)}>
                      {STATE_OPTIONS.map((option) => <option key={option.value || 'all'} value={option.value}>{option.label}</option>)}
                    </Select>
                    <Select aria-label="Source class" value={origin} onChange={(event) => setFilter('origin', event.target.value)}>
                      {SOURCE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </Select>
                    <Select
                      size="compact"
                      aria-label="Sort personalized bets"
                      value={sortOption.value}
                      onChange={(event) => setSort(event.target.value)}
                    >
                      {SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </Select>
                    <div>
                      <Input
                        aria-label="Sport"
                        list="personalized-bets-sports"
                        placeholder="All sports"
                        value={sport}
                        onChange={(event) => setFilter('sport', event.target.value)}
                      />
                      <datalist id="personalized-bets-sports">
                        {sports.map((name) => <option key={name} value={name} />)}
                      </datalist>
                    </div>
                    </div>
                  </details>
                )}
                actions={activeFilters ? <Button size="compact" variant="ghost" onClick={clearBetFilters}>Clear filters</Button> : null}
              />

              <span className={styles.visuallyHidden} aria-live="polite">{selectedRows.length} bets selected</span>
              {selectedRows.length ? (
                <BulkActionBar
                  count={selectedRows.length}
                  noun="bet"
                  onClear={() => clearSelectedRows(true)}
                  pending={bulkVisibilityMutation.isPending}
                  sticky
                  ariaLabel="Bulk visibility actions"
                  announce={false}
                >
                  <Button
                    size="compact"
                    variant="secondary"
                    disabled={bulkVisibilityMutation.isPending}
                    loading={bulkVisibilityMutation.isPending && bulkVisibilityMutation.variables === false}
                    onClick={() => bulkVisibilityMutation.mutate(false)}
                  >
                    Unhide selected
                  </Button>
                  <Button
                    size="compact"
                    variant="secondary"
                    disabled={bulkVisibilityMutation.isPending}
                    loading={bulkVisibilityMutation.isPending && bulkVisibilityMutation.variables === true}
                    onClick={() => bulkVisibilityMutation.mutate(true)}
                  >
                    Hide selected
                  </Button>
                </BulkActionBar>
              ) : null}

              <DataTable
                selectedKeys={selectedKeys}
                className={styles.betsTable}
                data={posts}
                columns={columns}
                loading={postsQuery.isLoading || postsQuery.isPlaceholderData}
                density="default"
                selectable
                label="Surfaced personalized bets"
                getRowLabel={postSummary}
                onSelectionChange={handleSelectionChange}
                onRowClick={(post) => openInspector(post)}
                emptyMessage={(
                  <StatePanel
                    compact
                    state={activeFilters ? 'search-empty' : 'empty'}
                    title={activeFilters ? 'No personalized bets match these filters' : 'No surfaced personalized bets'}
                    description={activeFilters ? 'Clear one or more filters to widen the results.' : 'Eligible generated bets will appear here.'}
                    onClear={activeFilters ? clearBetFilters : undefined}
                  />
                )}
              />
              <Pagination page={Math.min(page, pageCount)} pageCount={pageCount} onPageChange={(next) => setFilter('page', String(next))} label="Personalized bets pages" />
            </section>
          )}
        </>
      )}
      </div>

      <Drawer
        open={Boolean(inspectedPost)}
        title="Personalized bet details"
        description={inspectedPost ? sourceLabel(inspectedPost.content_origin) : undefined}
        side="right"
        onClose={() => setInspectedPost(null)}
        returnFocusRef={inspectorReturnRef}
        className={styles.inspectorDrawer}
      >
        {inspectedPost ? (
          <div className={styles.inspectorContent}>
            <BrandedFeedPreview item={inspectorPreviewItem(inspectedPost)} title="Rendered feed preview" showModeSelector={false} allowFullscreen={false} />

            <section className={styles.detailSection} aria-labelledby="selections-heading">
              <h3 id="selections-heading">Selections and events</h3>
              {selectionsForPost(inspectedPost).length ? (
                <ol className={styles.selectionList}>
                  {selectionsForPost(inspectedPost).map((selection, index) => (
                    <li key={`${selectionName(selection)}-${index}`}>
                      <strong>{selectionName(selection)}</strong>
                      <span>{eventName(selection) || 'Event not available'}</span>
                    </li>
                  ))}
                </ol>
              ) : <p>No rendered selections are available.</p>}
            </section>

            <section className={styles.detailSection} aria-labelledby="eligibility-heading">
              <h3 id="eligibility-heading">Eligibility and lifecycle</h3>
              <dl className={styles.detailList}>
                <div><dt>Eligibility</dt><dd>{eligibilityLabel(inspectedPost.eligibility_reason)}</dd></div>
                {inspectedPost.content_origin === 'personalized_recommendation' ? <div><dt>Aggregate support</dt><dd>{supportCount(inspectedPost)}</dd></div> : null}
                <div><dt>State</dt><dd>{postState(inspectedPost).label}</dd></div>
                <div><dt>Expires</dt><dd>{formatDateTime(inspectedPost.expires_at)}</dd></div>
                <div><dt>Updated</dt><dd>{formatDateTime(inspectedPost.updated_at)}</dd></div>
                <div><dt>Surfaces</dt><dd>{surfaceTargets(inspectedPost).join(', ') || 'Default surface'}</dd></div>
              </dl>
            </section>

            <section className={styles.detailSection} aria-labelledby="rank-inputs-heading">
              <h3 id="rank-inputs-heading">Rank inputs</h3>
              <dl className={styles.detailList}>
                <div><dt>Global score</dt><dd>{firstNumber(inspectedPost.rank_inputs?.global_score, inspectedPost.score) ?? '—'}</dd></div>
                <div><dt>Feed position</dt><dd>{feedPosition({ ...inspectedPost, is_pinned: Boolean(orderOverride?.pin_to_top), order_override: orderOverride, override: orderOverride })}</dd></div>
                <div><dt>Ranking mode</dt><dd>Global + followed creators</dd></div>
                <div><dt>Source class</dt><dd>{sourceLabel(inspectedPost.content_origin)}</dd></div>
              </dl>
            </section>

            <section className={styles.detailSection} aria-labelledby="pin-heading">
              <h3 id="pin-heading">Time-bound pin</h3>
              {orderDetailQuery.isLoading ? (
                <StatePanel compact state="loading" title="Loading pin settings…" />
              ) : orderDetailQuery.isError ? (
                <StatePanel
                  compact
                  state="error"
                  title="Pin settings could not load"
                  description={extractErrorMessage(orderDetailQuery.error, 'Surface-specific ordering is unavailable.')}
                  onRetry={orderDetailQuery.refetch}
                />
              ) : null}
              <div className={styles.pinControls}>
                <Field label="Surface">
                  <Select disabled={orderDetailQuery.isLoading || orderDetailQuery.isError} value={pinSurface} onChange={(event) => setPinSurface(event.target.value)}>
                    <option value="">Choose a surface</option>
                    {surfaces.map((item) => <option key={item.id || item.slug} value={item.slug}>{item.name}</option>)}
                  </Select>
                </Field>
                <Field label="Feed position" help="Use the position this pin should hold on the selected surface.">
                  <Input disabled={orderDetailQuery.isLoading || orderDetailQuery.isError} type="number" min="1" step="1" value={pinRank} onChange={(event) => setPinRank(event.target.value)} />
                </Field>
                <Field label="Pin until" help="Pins must have a future end time.">
                  <Input disabled={orderDetailQuery.isLoading || orderDetailQuery.isError} type="datetime-local" value={pinUntil} onChange={(event) => setPinUntil(event.target.value)} />
                </Field>
                <div className={styles.pinActions}>
                  <Button variant="secondary" disabled={orderDetailQuery.isLoading || orderDetailQuery.isError} onClick={savePin} loading={pinMutation.isPending}>
                    <Pin size={16} aria-hidden="true" /> Save pin
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={orderDetailQuery.isLoading || orderDetailQuery.isError || !orderOverride?.pin_to_top}
                    loading={resetPinMutation.isPending}
                    onClick={() => {
                      if (!pinSurface) {
                        setOperationError('Choose a surface before resetting the pin.')
                        return
                      }
                      resetPinMutation.mutate({ post: inspectedPost, targetSurface: pinSurface })
                    }}
                  >
                    <RotateCcw size={16} aria-hidden="true" /> Reset pin
                  </Button>
                </div>
              </div>
            </section>

            <AdvancedDisclosure label="Technical details">
              <dl className={styles.detailList}>
                <div><dt>Post ID</dt><dd>{inspectedPost.id}</dd></div>
                <div><dt>Content origin</dt><dd>{inspectedPost.content_origin}</dd></div>
                <div><dt>Ranking version</dt><dd>global_follow_v2</dd></div>
              </dl>
            </AdvancedDisclosure>
          </div>
        ) : null}
      </Drawer>

    </PageLayout>
  )
}

function GeneratedPostsPage() {
  const [searchParams] = useSearchParams()
  if (searchParams.get('tab') === 'policy') return <Navigate to={POLICY_CONFIGURATION_PATH} replace />
  return <GeneratedPostsWorkspace />
}

export default GeneratedPostsPage
